import {
  AssetDashboardKpis,
  AssetTypeRecord,
  AssetRecord,
  AssetLocationHistory,
  AssetInspectionRecord,
  AssetMaintenanceEvent,
  CalibrationEvent,
} from './types';

const API_BASE = '/api/assets';

export const assetsService = {
  async getDashboardKpis(): Promise<AssetDashboardKpis> {
    const res = await fetch(`${API_BASE}/dashboard/kpis`);
    if (!res.ok) throw new Error('Failed to fetch dashboard KPIs');
    return res.json();
  },

  async getAssetTypes(): Promise<AssetTypeRecord[]> {
    const res = await fetch(`${API_BASE}/types`);
    if (!res.ok) throw new Error('Failed to fetch asset types');
    return res.json();
  },

  async getAssets(filters?: { domain?: string; status?: string; search?: string }): Promise<AssetRecord[]> {
    const query = new URLSearchParams();
    if (filters?.domain) query.append('domain', filters.domain);
    if (filters?.status) query.append('status', filters.status);
    if (filters?.search) query.append('search', filters.search);

    const res = await fetch(`${API_BASE}?${query.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch assets');
    return res.json();
  },

  async getAssetById(id: string): Promise<AssetRecord> {
    const res = await fetch(`${API_BASE}/${id}`);
    if (!res.ok) throw new Error('Failed to fetch asset');
    return res.json();
  },

  async createAsset(data: {
    asset_number: string;
    asset_type_id: string;
    asset_domain?: string;
    name: string;
    description?: string;
    manufacturer?: string;
    model?: string;
    serial_number?: string;
    criticality?: string;
    owner_user_name?: string;
    department?: string;
    current_location?: string;
    aircraft?: {
      registration_mark: string;
      msn: string;
      icao_type_code?: string;
      max_takeoff_weight?: number;
    };
  }): Promise<AssetRecord> {
    const res = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create asset');
    }
    return res.json();
  },

  async updateAssetStatus(id: string, status: string, notes?: string): Promise<AssetRecord> {
    const res = await fetch(`${API_BASE}/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, notes }),
    });
    if (!res.ok) throw new Error('Failed to update asset status');
    return res.json();
  },

  async getAssetLocations(assetId: string): Promise<AssetLocationHistory[]> {
    const res = await fetch(`${API_BASE}/${assetId}/locations`);
    if (!res.ok) throw new Error('Failed to fetch asset locations');
    return res.json();
  },

  async moveAsset(
    assetId: string,
    data: {
      location_name: string;
      location_type?: string;
      reason: string;
      moved_by_name?: string;
    }
  ): Promise<AssetRecord> {
    const res = await fetch(`${API_BASE}/${assetId}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to relocate asset');
    }
    return res.json();
  },

  async getInspections(): Promise<AssetInspectionRecord[]> {
    const res = await fetch(`${API_BASE}/inspections`);
    if (!res.ok) throw new Error('Failed to fetch inspections');
    return res.json();
  },

  async scheduleInspection(data: {
    asset_id: string;
    inspection_type: string;
    planned_date: string;
    inspector_name: string;
  }): Promise<AssetInspectionRecord> {
    const res = await fetch(`${API_BASE}/inspections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to schedule inspection');
    }
    return res.json();
  },

  async completeInspection(
    id: string,
    data: {
      actual_date: string;
      result: string;
      condition_rating: string;
      notes?: string;
    }
  ): Promise<AssetInspectionRecord> {
    const res = await fetch(`${API_BASE}/inspections/${id}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to complete inspection');
    }
    return res.json();
  },

  async getMaintenanceEvents(): Promise<AssetMaintenanceEvent[]> {
    const res = await fetch(`${API_BASE}/maintenance-events`);
    if (!res.ok) throw new Error('Failed to fetch maintenance events');
    return res.json();
  },

  async recordMaintenanceEvent(data: {
    asset_id: string;
    work_order_number: string;
    event_type: string;
    event_date: string;
    flight_hours_at_event?: number;
    flight_cycles_at_event?: number;
    description: string;
    performed_by: string;
    verified_by?: string;
    maintenance_reference?: string;
  }): Promise<AssetMaintenanceEvent> {
    const res = await fetch(`${API_BASE}/maintenance-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to record maintenance event');
    }
    return res.json();
  },

  async getCalibrations(): Promise<CalibrationEvent[]> {
    const res = await fetch(`${API_BASE}/calibrations`);
    if (!res.ok) throw new Error('Failed to fetch calibrations');
    return res.json();
  },

  async recordCalibration(data: {
    asset_id: string;
    calibration_standard: string;
    performed_by: string;
    calibration_date: string;
    expiry_date: string;
    certificate_number: string;
    result?: string;
    tolerance_accuracy?: string;
  }): Promise<CalibrationEvent> {
    const res = await fetch(`${API_BASE}/calibrations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to record calibration');
    }
    return res.json();
  },
};
